import { Outlet, createRootRouteWithContext, useRouterState } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { IAuthContext } from '../contexts/AuthContext';
import { isBrowserOnlyPath } from '../constants/browserOnlyPaths';
import * as Sentry from '@sentry/react';
// import { TanStackRouterDevtools } from '@tanstack/router-devtools';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// MIDEO Card App Store numeric ID (NOT the bundle/Team ID) for the iOS Smart
// App Banner. Distinct from the AASA appID — this drives the in-Safari banner.
const IOS_APP_STORE_ID = '6480421501';

type RouterContext = {
  auth: IAuthContext;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});

/**
 * Renders the iOS Smart App Banner ("Open in app") on every route, deep-linked
 * to the current page. Replaces ~26 hand-authored per-page <Helmet> tags
 * (CU-86aj1db1q). app-argument is derived from the LIVE pathname because portal
 * routes map 1:1 to the mobile app's Expo Router routes — so the path IS the
 * app deep-link. Deriving it (vs. hand-authoring) also fixed a stale value
 * (/card/cardReview previously pointed at a non-existent /card/cardPreview).
 *
 * Note: this is the in-Safari fallback. The primary deep-link path is Universal
 * Links via the AASA catch-all — tapping a link from SMS/Mail opens the app
 * directly without a Safari hop.
 *
 * ⚠️ Suppressed on browser-only routes (CU-86akjut0z). The AASA exclusions stop
 * the OS from handing those URLs to the app, but this banner is a SECOND door
 * onto the same failure: it offers a one-tap hop into the app carrying the path
 * as app-argument. On /concierge that spends the oobCode exactly as an
 * intercepted Universal Link would. Both doors or neither.
 */
export function SmartAppBanner() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Same list the AASA exclusions are built from, so the two cannot disagree.
  if (isBrowserOnlyPath(pathname)) return null;

  // Historical app-argument format is the path WITHOUT the leading slash,
  // e.g. "{patientId}/card/cardReview". Strip it; "/" → "" (app opens to root).
  const appArgument = pathname.replace(/^\//, '');

  return (
    <Helmet>
      <meta name="apple-itunes-app" content={`app-id=${IOS_APP_STORE_ID}, app-argument=${appArgument}`} />
    </Helmet>
  );
}

/**
 * A Firebase Auth uid: 28 characters of [A-Za-z0-9]. No portal route name is
 * shaped like this, which is what lets us tell `/{uid}/card` apart from
 * `/concierge` without hardcoding a route list that would rot (CU-86akjut0z).
 */
const UID_SEGMENT = /^[A-Za-z0-9]{20,}$/;

function ErrorFallback() {
  // Extract patientId from the URL since route context is unavailable after a
  // render crash. MOST authenticated routes follow the pattern /{patientId}/...
  //
  // ⚠️ But top-level routes do NOT — /concierge, /login, /enroll, /privacy,
  // /home, /welcome, /passwordless, /testErrors and /sessions all have a route
  // NAME in segment 1. Taking it blindly built href="/concierge", so "Go Home"
  // reloaded the very page that had just crashed: an infinite loop out of the
  // one control the page offers for recovery.
  //
  // Found by device testing 2026-09-21 (Sentry MIDEO-UNIFIED-BB): a crash on
  // /concierge left the patient with a dead "Go Home" button.
  const firstSegment = window.location.pathname.split('/')[1];
  const patientId = UID_SEGMENT.test(firstSegment ?? '') ? firstSegment : null;
  const homeHref = patientId ? `/${patientId}` : '/';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-stone-100 px-4 text-center">
      <h1 className="mb-2 text-2xl font-bold text-gray-800">Something went wrong</h1>
      <p className="mb-6 text-gray-600">
        We're sorry for the inconvenience. Please return home and try again. If this issue persists, you can report it
        using the feedback button in the bottom-right corner of the screen, or contact support.
      </p>
      <a href={homeHref} className="rounded-md bg-sky-700 px-6 py-3 text-white hover:bg-sky-800">
        Go Home
      </a>
    </div>
  );
}

function RootComponent() {
  return (
    <div className="bg-stone-100">
      <SmartAppBanner />
      <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
        <Outlet />
      </Sentry.ErrorBoundary>
      {import.meta.env.VITE_ENV === 'LOCAL' ||
        (import.meta.env.VITE_ENV === 'staging' && (
          <>
            {/* <TanStackRouterDevtools position="bottom-left" initialIsOpen={false} /> */}
            {/* <ReactQueryDevtools initialIsOpen={true} position="bottom" buttonPosition="bottom-right" /> */}
          </>
        ))}
    </div>
  );
}
